<template>
  <div class="finance-dashboard" :class="{ 'finance-dashboard--dark': isDarkSkin }">

    <div class="finance-dashboard__toolbar">
      <filter-card :filter-key="viewKey" dashboard single-row v-model="filter" v-bind:fields="filterFields"/>
      <calendar-bar v-model="dateRange" @input="scheduleRefresh"/>
    </div>
    <!-- One-click department pills — fill the BU filter like the sales dashboard -->
    <dept-quick-filter v-model="filter.buIds" :org-ids="filter.orgIds"/>

    <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">

      <!-- Container-driven grid (like the Monetary dashboard): all KPIs share
           ONE row while they fit, wrapping 2×2 → 1-col only when the actual
           available width (sidebar included) gets too narrow. -->
      <div class="kpi-grid">
        <div class="kpi-cell">
          <div class="kpi-card kpi-card--pending">
            <div class="kpi-card__icon"><feather-icon icon="ClockIcon" size="24"/></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('finance.pendingReview') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(stats.pendingAmount) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>{{ $t('Count') }}: {{ stats.pendingCount }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </div>

        <div class="kpi-cell">
          <div class="kpi-card kpi-card--success">
            <div class="kpi-card__icon"><feather-icon icon="CheckCircleIcon" size="24"/></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('finance.received') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(stats.approvedAmount) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>{{ $t('Count') }}: {{ stats.approvedCount }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </div>

        <div class="kpi-cell">
          <div class="kpi-card kpi-card--danger">
            <div class="kpi-card__icon"><feather-icon icon="XCircleIcon" size="24"/></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('finance.canceled') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(stats.canceledAmount) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>{{ $t('Count') }}: {{ stats.canceledCount }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </div>

        <!-- TXF (taxes): CRM-bookkeeping money — always shown, never inside
             the finance review buckets above -->
        <div class="kpi-cell">
          <div class="kpi-card kpi-card--txf">
            <div class="kpi-card__icon"><i class="fa-solid fa-file-invoice-dollar" style="font-size: 22px;"></i></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('monetaryDashboard.txf') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(stats.txfAmount) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>{{ $t('Count') }}: {{ stats.txfCount }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </div>
      </div>

      <b-card no-body class="mt-1">
        <b-card-header class="pb-50 d-flex align-items-center justify-content-between flex-wrap">
          <h5 class="mb-0">{{ $t('finance.approvedDeposits') }}</h5>
          <!-- Client search (name / email / account) -->
          <div class="fd-search">
            <feather-icon icon="SearchIcon" size="14" class="fd-search__icon"/>
            <b-form-input v-model="searchText" :debounce="500" :placeholder="$t('finance.searchClient')"
                          size="sm" class="fd-search__input"/>
            <b-link v-if="searchText" class="fd-search__clear" @click="searchText = ''">
              <feather-icon icon="XIcon" size="13"/>
            </b-link>
          </div>
          <!-- Segmented finance-status filter with live counts -->
          <div class="fd-seg2">
            <button v-for="s in segments" :key="s.value" type="button" class="fd-seg2__opt"
                    :class="[{ 'is-active': segment === s.value }, `fd-seg2__opt--${s.tone}`]"
                    @click="segment = s.value">
              <i :class="s.icon"></i>{{ s.label }}
              <span v-if="s.count != null" class="fd-seg2__count">{{ s.count }}</span>
            </button>
          </div>
          <b-button size="sm" variant="outline-primary" class="ml-1" v-b-tooltip.hover :title="$t('columns.self')"
                    @click="openColumnsModal">
            <i class="fa-solid fa-bars"></i>
          </b-button>
        </b-card-header>
        <b-card-body>
          <monetaries-table ref="finTable" :filters="financeFilters" :columns="columns" :finance-mode="true"
                            trans-type="finance" :has-selection="true" :lazy="false"
                            @finance-updated="onFinanceUpdated">
            <template #table-top-actions="{ selectedIds }">
              <div class="fd-bulk" v-if="Object.keys(selectedIds).length">
                <span class="fd-bulk__count">
                  <i class="fa-solid fa-list-check mr-25"></i>
                  {{ Object.keys(selectedIds).length }} {{ $t('finance.actionModal.selected') }}
                </span>
                <b-button size="sm" variant="primary" @click="openBulkActions">
                  <i class="fa-solid fa-sliders mr-25"></i>{{ $t('finance.actions') }}
                </b-button>
                <b-button size="sm" variant="flat-secondary" @click="$refs.finTable.clearSelection()">
                  {{ $t('finance.actionModal.clear') }}
                </b-button>
              </div>
            </template>
          </monetaries-table>
        </b-card-body>
      </b-card>

    </b-overlay>

    <!-- Column show/hide/reorder for the finance table (finance-mode still always
         appends the finance status/comment/actions columns). -->
    <columns-modal ref="columns-modal" v-model="columns" v-bind:columns-names="columnsNames"
                   v-bind:columns-default="financeColumnsDefault"/>
  </div>
</template>

<script>
import CalendarBar from '@/components/widget/CalendarBar';
import DeptQuickFilter from '@/components/widget/DeptQuickFilter';
import mixinBase from '@/components/mixin/mixinBase';
import { mapActions, mapGetters } from 'vuex';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import FilterCard from '@/components/widget/FilterCard';
import MonetariesTable from '@/components/tables/MonetariesTable';
import MonetaryDashboardSearchRequest from '@/model/Dashboard/MonetaryDashboardSearchRequest';
import MonetaryDashboardFilterFields from '@/model/filter/MonetaryDashboardFilterFields';
import ColumnsModal from '@/components/modal/columnsModal';
import Monetaries from '@/model/monetary/Monetary';

const REFRESH_DEBOUNCE_MS = 250;

export default {
  name: 'FinanceDashboard',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  components: { CalendarBar, DeptQuickFilter, FilterCard, MonetariesTable, ColumnsModal },
  data: () => ({
    filter: MonetaryDashboardSearchRequest(),
    filterFields: [],
    // Column show/hide/reorder prefs (persisted per-route by columns-modal).
    columns: [],
    columnsNames: [
      ...Object.keys(Monetaries()),
      'clientDetails.accountId', 'clientDetails.clientId', 'clientDetails.email', 'clientDetails.country', 'clientDetails.affiliate',
      'paymentDetails.processorName', 'paymentDetails.comments',
    ],
    dateRange: { from: null, until: null },
    loading: false,
    inflight: false,
    pendingFetch: false,
    refreshTimer: null,
    segment: 'PENDING',
    searchText: '',
    stats: {
      pendingCount: 0, pendingAmount: 0,
      approvedCount: 0, approvedAmount: 0,
      canceledCount: 0, canceledAmount: 0,
      // TXF (taxes) — own KPI, never inside the buckets above
      txfCount: 0, txfAmount: 0,
    },
  }),
  watch: {
    filter: { handler: 'scheduleRefresh', deep: true },
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    viewKey() { return this.$route.name || 'finance-dashboard'; },
    segments() {
      return [
        { value: 'PENDING', label: this.$t('financeStatuses.PENDING'), tone: 'warning', icon: 'fa-solid fa-clock', count: this.stats.pendingCount },
        { value: 'APPROVED', label: this.$t('financeStatuses.APPROVED'), tone: 'success', icon: 'fa-solid fa-circle-check', count: this.stats.approvedCount },
        { value: 'CANCELED', label: this.$t('financeStatuses.CANCELED'), tone: 'danger', icon: 'fa-solid fa-circle-xmark', count: this.stats.canceledCount },
        { value: 'ALL', label: this.$t('finance.all'), tone: 'primary', icon: 'fa-solid fa-layer-group', count: null },
      ];
    },
    // Keys enabled by default in the column picker (mirrors the previous
    // hardcoded finance column set); everything else is available but off.
    financeColumnsDefault() {
      return [
        'clientDetails.accountId', 'clientDetails', 'clientDetails.email', 'amount', 'currency',
        'depositorUserId', 'isFtd', 'realType', 'paymentDetails.processorName', 'registrationTime', 'status',
      ];
    },
    financeFilters() {
      return {
        type: 'DEPOSIT',
        orgIds: this.filter.orgIds,
        businessUnit: this.filter.buIds,
        registrationTime: {
          start: this.$moment(this.dateRange.from).toServerDateTimeFormat(),
          end: this.$moment(this.dateRange.until).toServerDateTimeFormat(),
        },
        realType: this.filter.realOnly === false ? null : ['REAL'],
        status: ['APPROVED', 'CANCELED'],
        // TXF rows show in the finance table too (under Pending until finance
        // confirms them) unless the "Exclude TXF" filter is ticked.
        action: this.filter.excludeTxf ? ['CREDIT_LINE'] : ['CREDIT_LINE', 'TAXES'],
        isTest: false,
        financeStatus: this.segment === 'ALL' ? null : [this.segment],
        search: this.searchText && this.searchText.trim() ? this.searchText.trim() : null,
      };
    },
  },
  created() {
    this.filterFields = MonetaryDashboardFilterFields();
    this.dateRange = {
      from: this.$moment().startOf('month').format('YYYY-MM-DD'),
      until: this.$moment().endOf('month').format('YYYY-MM-DD'),
    };
  },
  mounted() { this.$root.$on('dashboard::refresh', this.scheduleRefresh); },
  beforeDestroy() {
    this.$root.$off('dashboard::refresh', this.scheduleRefresh);
    if (this.refreshTimer) { clearTimeout(this.refreshTimer); this.refreshTimer = null; }
  },
  methods: {
    ...mapActions('dashboard', ['getFinanceDashboard']),
    openColumnsModal() {
      this.$refs['columns-modal'].showModal();
    },
    scheduleRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DEBOUNCE_MS);
    },
    refresh() {
      if (!this.dateRange.from) return;
      if (this.inflight) { this.pendingFetch = true; return; }
      this.doFetch();
    },
    doFetch() {
      const payload = { ...this.filter, range: { from: this.dateRange.from, until: this.dateRange.until } };
      this.inflight = true;
      this.loading = true;
      this.getFinanceDashboard(payload)
        .then(
          data => this.setData(data),
          err => this.showServerErrorNotification('getFinanceDashboard', 'financeDashboard', err)
        )
        .finally(() => {
          this.inflight = false;
          this.loading = false;
          if (this.pendingFetch) { this.pendingFetch = false; this.doFetch(); }
        });
    },
    setData(data) {
      if (!data) return;
      this.stats.pendingCount = data.pendingCount || 0;
      this.stats.pendingAmount = data.pendingAmount || 0;
      this.stats.approvedCount = data.approvedCount || 0;
      this.stats.approvedAmount = data.approvedAmount || 0;
      this.stats.canceledCount = data.canceledCount || 0;
      this.stats.canceledAmount = data.canceledAmount || 0;
      this.stats.txfCount = data.txfCount || 0;
      this.stats.txfAmount = data.txfAmount || 0;
    },
    onFinanceUpdated() {
      // A finance action changed a transaction — refresh the KPI cards.
      this.scheduleRefresh();
    },
    openBulkActions() {
      const table = this.$refs.finTable;
      // mixinTable stores selections as {active, id, item} objects (or
      // `all: true`) — getAllSelectedItems resolves both to row objects.
      table.openFinanceActionsBulk(table.getAllSelectedItems());
    },
  },
};
</script>

<style scoped lang="scss">
.finance-dashboard {
  position: relative;
  &__toolbar { display: flex; flex-direction: column; gap: 0.5rem; }
}
.kpi-grid {
  display: grid;
  /* Container-driven: columns wrap to the ACTUAL available width (whether the
     sidebar is open or not) instead of Bootstrap's viewport breakpoints — all
     4 KPIs share one row while they fit, then 2×2, then a single column. */
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: 0.75rem;
  margin: 0.5rem 0 1rem;
}
.kpi-cell {
  display: flex;
  min-width: 0; /* let cards shrink instead of overflowing */
}
.kpi-cell .kpi-card {
  flex: 1;
  min-width: 0;
}

/* Client search box in the table header */
.fd-search {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-width: 240px;

  &__icon {
    position: absolute;
    left: 10px;
    color: #9a9dad;
    pointer-events: none;
  }
  &__input {
    padding-left: 30px;
    padding-right: 26px;
    border-radius: 10px;
  }
  &__clear {
    position: absolute;
    right: 8px;
    display: inline-flex;
    color: #9a9dad;
    &:hover { color: #ea5455; }
  }
}

/* Segmented finance-status filter (pill switch with counts) */
.fd-seg2 {
  display: inline-flex;
  background: #f0f1f7;
  border-radius: 12px;
  padding: 4px;
  gap: 4px;

  &__opt {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    border: none;
    background: transparent;
    color: #6e6b7b;
    font-weight: 600;
    font-size: 0.8rem;
    padding: 0.42rem 0.85rem;
    border-radius: 9px;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;

    &:hover { color: #2b2f42; }
    &.is-active {
      background: #fff;
      box-shadow: 0 3px 9px rgba(20, 24, 50, 0.13);
    }
    &--warning.is-active { color: #f59e0b; }
    &--success.is-active { color: #28c76f; }
    &--danger.is-active { color: #ea5455; }
    &--primary.is-active { color: #7367f0; }
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: rgba(110, 117, 159, 0.14);
    font-size: 0.68rem;
    font-weight: 700;
  }
}

/* Bulk action bar (shows when rows are ticked) */
.fd-bulk {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.75rem;
  border-radius: 12px;
  background: rgba(115, 103, 240, 0.08);
  border: 1px solid rgba(115, 103, 240, 0.18);

  &__count {
    font-size: 0.82rem;
    font-weight: 700;
    color: #5e5873;
    margin-right: auto;
  }
}

.finance-dashboard--dark {
  .fd-seg2 {
    background: rgba(255, 255, 255, 0.06);
    &__opt { color: #b4b7bd; &:hover { color: #fff; } &.is-active { background: #283046; } }
    &__count { background: rgba(255, 255, 255, 0.1); }
  }
  .fd-bulk { border-color: rgba(158, 149, 245, 0.25); &__count { color: #d0d2d6; } }
  .fd-bulk__count { color: #d0d2d6; }
}

.kpi-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.35rem;
  border-radius: 18px;
  color: #fff;
  overflow: hidden;
  box-shadow: 0 12px 28px -14px rgba(20, 24, 50, 0.28), 0 2px 4px rgba(20, 24, 50, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  min-height: 118px;

  &:hover { transform: translateY(-3px); box-shadow: 0 18px 40px -16px rgba(20, 24, 50, 0.35), 0 4px 8px rgba(20, 24, 50, 0.08); }

  &__icon {
    position: relative; z-index: 2;
    display: flex; align-items: center; justify-content: center;
    width: 52px; height: 52px; border-radius: 14px;
    background: rgba(255, 255, 255, 0.22); backdrop-filter: blur(6px); flex-shrink: 0;
  }
  &__body { position: relative; z-index: 2; flex: 1; min-width: 0; }
  &__title {
    font-size: 0.82rem; font-weight: 500; text-transform: uppercase; letter-spacing: 0.04em;
    opacity: 0.86; margin-bottom: 0.25rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  &__value { font-size: 1.55rem; font-weight: 700; line-height: 1.15; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__footer { margin-top: 0.45rem; font-size: 0.76rem; opacity: 0.9; display: flex; align-items: center; }
  &__glow { position: absolute; top: -40%; right: -20%; width: 220px; height: 220px; border-radius: 50%; background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%); pointer-events: none; z-index: 1; }

  &--success { background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%); }
  &--danger { background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%); }
  &--pending { background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%); }
  &--txf { background: linear-gradient(135deg, #2dd4bf 0%, #14b8a6 55%, #0f766e 100%); }
}

@media (max-width: 576px) {
  .kpi-card { min-height: auto; padding: 1rem; &__value { font-size: 1.3rem; } }
}
</style>
